import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  ViewChild,
} from '@angular/core';
import {
  extendSortingDataAccessor,
  ObservableDataSource,
  PipesModule,
  timestampSortingAccessor,
} from '@principle-theorem/ng-shared';
import {
  IBrand,
  IPractice,
  IPatient,
  IMohPreApprovalFundingRequest,
  FundingRequestStatus,
  MOH_CLAIM_TYPE_LABEL_MAP,
  MohClaimType,
} from '@principle-theorem/principle-core/interfaces';
import {
  WithRef,
  HISTORY_DATE_FORMAT,
  INamedDocument,
} from '@principle-theorem/shared';
import { ReplaySubject, BehaviorSubject, Observable } from 'rxjs';
import { MohPreApprovalService } from '../../../moh/moh-pre-approval.service';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { compact } from 'lodash';
import { map } from 'rxjs/operators';
import { RouterModule } from '@angular/router';
import { LetDirective } from '@ngrx/component';
import { NgMaterialModule } from '@principle-theorem/ng-material';
import {
  StatusLabelComponent,
  EmptyStateComponent,
} from '@principle-theorem/ng-principle-shared';
import { MomentModule } from 'ngx-moment';
import {
  FundingRequest,
  TreatmentStep,
} from '@principle-theorem/principle-core';

@Component({
  selector: 'pr-moh-approval-requests-table',
  imports: [
    NgMaterialModule,
    PipesModule,
    MomentModule,
    StatusLabelComponent,
    RouterModule,
    EmptyStateComponent,
    LetDirective,
  ],
  templateUrl: './moh-approval-requests-table.component.html',
  styleUrl: './moh-approval-requests-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MohPreApprovalService],
})
export class MohApprovalRequestsTableComponent {
  private _brand$ = new ReplaySubject<WithRef<IBrand>>(1);
  private _practice$ = new ReplaySubject<WithRef<IPractice>>(1);
  private _patient$ = new BehaviorSubject<WithRef<IPatient> | undefined>(
    undefined
  );
  readonly dateFormat = HISTORY_DATE_FORMAT;
  mohService = inject(MohPreApprovalService);
  dataSource: ObservableDataSource<WithRef<IMohPreApprovalFundingRequest>>;
  noRequests$: Observable<boolean>;
  displayedColumns$: Observable<string[]>;
  pageSizeOptions = [25, 50, 100];
  pageSize = 25;

  @Input()
  set brand(brand: WithRef<IBrand>) {
    if (brand) {
      this._brand$.next(brand);
    }
  }
  @Input()
  set practice(practice: WithRef<IPractice>) {
    if (practice) {
      this._practice$.next(practice);
    }
  }
  @Input()
  set patient(patient: WithRef<IPatient> | undefined) {
    this._patient$.next(patient);
  }

  @ViewChild(MatSort)
  set tableSort(sort: MatSort) {
    this.dataSource.sort = sort;
  }

  @ViewChild(MatPaginator)
  set tablePaginator(paginator: MatPaginator) {
    this.dataSource.paginator = paginator;
  }

  constructor() {
    this.dataSource = new ObservableDataSource(
      this.mohService.getPreApprovalRequests$(this._practice$, this._patient$)
    );
    this.dataSource.sortingDataAccessor = extendSortingDataAccessor(
      (data, sortHeaderId) => this._sortingDataAccessor(data, sortHeaderId)
    );
    this.noRequests$ = this.dataSource.filteredLength$.pipe(
      map((length) => length === 0)
    );
    this.displayedColumns$ = this._patient$.pipe(
      map((patient) =>
        compact([
          'status',
          'createdAt',
          'claimType',
          'approvalNumber',
          !patient ? 'patient' : undefined,
          'treatmentStep',
          'actions',
        ])
      )
    );
  }

  getStatusColour(status: FundingRequestStatus): string {
    return FundingRequest.getStatusColour(status);
  }

  getClaimTypeLabel(claimType: MohClaimType): string {
    return MOH_CLAIM_TYPE_LABEL_MAP[claimType];
  }

  requestNotSubmitted(
    request: WithRef<IMohPreApprovalFundingRequest>
  ): boolean {
    return request.status !== FundingRequestStatus.Submitted;
  }

  requestNotInDraft(request: WithRef<IMohPreApprovalFundingRequest>): boolean {
    return request.status !== FundingRequestStatus.Draft;
  }

  canCancel(request: WithRef<IMohPreApprovalFundingRequest>): boolean {
    return FundingRequest.canCancel(request);
  }

  patientLink$(patient: INamedDocument<IPatient>): Observable<string[]> {
    return this._brand$.pipe(
      map((brand) => ['/', brand.slug, 'patients', patient.ref.id])
    );
  }

  treatmentPlanLink$(
    request: WithRef<IMohPreApprovalFundingRequest>
  ): Observable<string[]> {
    const planRef = TreatmentStep.treatmentPlanRef({
      ref: request.data.stepRef,
    });
    return this._brand$.pipe(
      map((brand) => [
        '/',
        brand.slug,
        'patients',
        request.patient.ref.id,
        'treatment-plans',
        planRef.id,
      ])
    );
  }

  private _sortingDataAccessor(
    data: WithRef<IMohPreApprovalFundingRequest>,
    sortHeaderId: string
  ): string | number | undefined {
    switch (sortHeaderId) {
      case 'createdAt':
        return timestampSortingAccessor(data.createdAt);
      default:
        return;
    }
  }
}
